import { createFileRoute } from "@tanstack/react-router";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { Section, SectionHeader, Breadcrumbs, CTABand, FAQ } from "@/components/site/Blocks";
import { buildHead } from "@/lib/seo-head";
import { DSCR_HELOC_PUBLIC_LABELS } from "@/lib/dscr-heloc/types";
import { HelocPricingTool } from "@/components/heloc/HelocPricingTool";
import { PageEssentials, PageOutro } from "@/components/site/PageEssentials";
import helocEquityAccessHero from "@/assets/hero/hero-investment-property-heloc-equity-access.jpg";

const URL = "/dscr-heloc-rates";
const KEYWORDS = [
  "investment property HELOC rates",
  "DSCR HELOC pricing",
  "rental property home equity loan rates",
  "second lien investment property rates",
  "investment property equity loan pricing",
  "non-owner occupied HELOC rates",
].join(", ");

const FAQS = [
  {
    q: "Is this pricing live?",
    a: "No. These options come from the current active wholesale pricing schedule, and the pricing tool shows its effective date on screen. Pricing is subject to change and is not a commitment, approval, or guarantee.",
  },
  {
    q: "Why is no APR shown?",
    a: "This tool shows the note rate, origination fee, and estimated principal and interest only. An APR is provided later with your scenario-specific cost detail.",
  },
  {
    q: "Which lien positions are supported?",
    a: "Non-primary first lien and non-primary second lien investment-property equity pricing. Owner-occupied and consumer-purpose requests are not supported.",
  },
  {
    q: "Can I get a HELOC on a rental property I own free and clear?",
    a: "Yes. A paid-off rental can typically be financed in first-lien position, and the equity available depends on the property value, requested CLTV, and credit tier at the time of pricing. Terms are illustrative and subject to appraisal and full underwriting.",
  },
  {
    q: "Is the draw period fixed or does it vary by scenario?",
    a: "Draw and repayment structure vary by program and are shown in your scenario detail rather than as a single blanket figure. Confirm the specific structure for your CLTV and lien position before relying on it.",
  },
  {
    q: "Does a second-lien HELOC affect my first mortgage terms?",
    a: "No. Adding a second-lien equity line does not modify your existing first mortgage rate or payment; it sits behind it in priority. Some first-lien servicers require notice or consent, which your loan officer will confirm.",
  },
  {
    q: "What credit tier is typically required for investment property equity lines?",
    a: "Pricing is organized into FICO bands, and stronger credit tiers generally unlock higher CLTV and better terms. The pricing tool reflects the band you select and is not a guarantee of approval at that tier.",
  },
  {
    q: "Can the equity line be used across a whole rental portfolio?",
    a: "This tool prices a single subject property. Portfolio-wide facilities secured by multiple properties are a different structure and would be quoted separately with full underwriting.",
  },
  {
    q: "How quickly can an investment property equity line close?",
    a: "Timelines depend on title work, appraisal scheduling, and how quickly documentation is returned, and vary by lien position and state. There is no standard closing date promised on this page; ask your loan officer for a scenario-specific estimate.",
  },
  {
    q: "What CLTV bands are typically available on an investment property equity line?",
    a: "CLTV bands vary by lien position, property type, and credit tier, and the pricing tool will only return options for combinations currently supported. Higher CLTV requests generally carry a higher note rate to offset the added leverage risk.",
  },
  {
    q: "Can I use an investment property HELOC on a 2-4 unit property?",
    a: "Eligibility for multi-unit non-owner-occupied properties depends on the specific program and state; enter the property details into the tool to see which lien positions and CLTV bands return pricing for that unit count.",
  },
  {
    q: "Is there a minimum draw or minimum line amount?",
    a: "Program minimums and maximums vary by lien position and are shown in your scenario-specific detail rather than a single fixed number here. Confirm minimum draw and line-size requirements with your loan officer for the exact program you are pricing.",
  },
];

export const Route = createFileRoute("/dscr-heloc-rates")({
  head: () =>
    buildHead({
      title: "Investment Property HELOC Rates — DSCR HELOC Pricing Tool",
      description:
        "Price a non-owner-occupied first or second lien equity loan by FICO, CLTV, and term. Note rate, origination fee, and estimated P&I from the current wholesale pricing schedule.",
      url: URL,
      keywords: KEYWORDS,
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "Investment Property HELOC", url: "/dscr-heloc" },
        { name: "HELOC Rates", url: URL },
      ],
      service: { name: "Investment Property HELOC Pricing" },
      faqs: FAQS,
    }),
  component: HelocRates,
});

function HelocRates() {
  return (
    <SiteShell hideBreadcrumbs productLabel="Investor HELOC">
      <PageHero
        eyebrow="Investment Property HELOC Rates"
        title={DSCR_HELOC_PUBLIC_LABELS.rates}
        subtitle="Price a non-owner-occupied first or second lien equity loan by FICO band, combined loan-to-value, term, and origination fee option — from the current wholesale pricing schedule."
        primaryHref="/apply?product=heloc"
        image={{
          src: helocEquityAccessHero,
          alt: "Investment home with a visual showing equity accessed for another rental-property purchase",
        }}
      />
      <PageEssentials />
      <Breadcrumbs
        items={[
          { name: "Home", href: "/" },
          { name: "Investment Property HELOC", href: "/dscr-heloc" },
          { name: "HELOC Rates" },
        ]}
      />

      <HelocPricingTool id="pricing" surface="heloc-pricing-tool" />

      <Section id="second-lien">
        <SectionHeader
          eyebrow="Second position"
          title="Pricing a 2nd lien behind an existing mortgage?"
          intro="Second-position requests have their own product page covering combined loan-to-value, lien priority, and who the structure fits."
        />
        <p className="mt-6 max-w-3xl text-muted-foreground">
          <a className="font-semibold text-brand underline underline-offset-4" href="/dscr-second-lien-heloc">
            Open the DSCR second-lien HELOC page
          </a>
          .
        </p>
      </Section>

      <Section id="faq">
        <SectionHeader eyebrow="FAQ" title="Investment property HELOC pricing questions" />
        <div className="mt-8">
          <FAQ items={FAQS} />
        </div>
      </Section>

      <CTABand
        title="Get a scenario review on your equity request"
        body="Send the property address, value, existing lien balance, and target loan amount."
        href="/apply?product=heloc"
      />
    <PageOutro />
      </SiteShell>
  );
}
